"use client";

import { useMemo, useState } from "react";
import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type FilterReason,
  findSimiles,
  type SimileHit,
  type SimileKind,
  sentenceAround,
} from "@/lib/tool-kit/poetry/simile";

const BURNS = `O my Luve is like a red, red rose
That’s newly sprung in June;
O my Luve is like the melodie
That’s sweetly play’d in tune.`;

const WORDSWORTH = `I wandered lonely as a cloud
That floats on high o’er vales and hills,
When all at once I saw a crowd,
A host, of golden daffodils;`;

const TENNYSON = `He clasps the crag with crooked hands;
Close to the sun in lonely lands,
Ring’d with the azure world, he stands.
The wrinkled sea beneath him crawls;
He watches from his mountain walls,
And like a thunderbolt he falls.`;

const TRICKY =
  "She is as brave as a lion, and she runs like the wind. I like tea, but call me as soon as you can.";

const RE_NEWLINES = /\r\n?/g;

/** English strings. A translated content file overrides these through `ui`. */
export const SIMILE_FINDER_STRINGS = {
  textLabel: "Your poem or passage",
  textPlaceholder: TRICKY,
  samplesLabel: "Try a sample:",
  sampleBurns: "Burns, A Red, Red Rose",
  sampleWordsworth: "Wordsworth, Daffodils",
  sampleTennyson: "Tennyson, The Eagle",
  sampleTricky: "Tricky sentences",
  sampleBurnsText: BURNS,
  sampleWordsworthText: WORDSWORTH,
  sampleTennysonText: TENNYSON,
  sampleTrickyText: TRICKY,
  sampleCredit:
    "Samples are public-domain texts: Burns (1794), Wordsworth (1807), Tennyson (1851); the last is our own.",
  clear: "Clear",
  possibleLabel: "Show uncertain matches too",
  emptyPrompt: "Type or paste some text to look for similes.",
  heading: "Similes found",
  statLikely: "Likely similes",
  statPossible: "Possible",
  statSetAside: "Set aside",
  highlightLabel: "Your text with similes highlighted",
  legend: "Green: a likely simile. Amber: possible — check it.",
  noneFound:
    "No similes found. Metaphors have no marker word, so this tool cannot find them.",
  kinds: {
    "as-as": "as … as",
    like: "like",
    "adjective-as": "adjective + as",
  },
  confidence: { likely: "likely", possible: "possible" },
  compared: "compared to: {vehicle}",
  notes: {
    perception:
      "After a verb of perception (looks like, feels like) this is often a plain observation, not a figure of speech.",
    approximation:
      "“Nothing like…” and “something like…” often mean “not similar to” or “roughly”, not a figurative comparison.",
  },
  setAsideHeading: "Set aside",
  setAsideHint:
    "These contain a marker word but were filtered out. If one is really a simile, you can add it yourself.",
  reasons: {
    idiom: "fixed phrase (as soon as, as long as, as well as…)",
    degree: "a degree comparison, not a figure (as fast as he could)",
    verb: "“like” used as a verb (I like tea, would like to)",
    filler: "“like” used as filler (it was, like, cold)",
    pointing: "“like this / like that” points at something",
    example: "“like him / like you” gives an example (such as)",
  },
  metaphorNote:
    "Metaphors compare without a marker word (“time is a thief”), so a pattern cannot find them. A word list or grammar rule that flags “is a” would mark thousands of ordinary sentences.",
  methodNote:
    "Matching works from the words around “like” and “as … as”; there is no grammar parser, so some calls are guesses. A match marked possible deserves a second look.",
  copyList: "Copy list",
  copied: "Copied!",
  count: "one:{count} match|other:{count} matches",
} as const;

type Strings = typeof SIMILE_FINDER_STRINGS;

const ID = "simile";

function HitCard({
  hit,
  context,
  strings,
}: {
  hit: SimileHit;
  context: string;
  strings: Strings;
}) {
  return (
    <li className={`${CARD} space-y-1`}>
      <p className="text-foreground text-sm">
        <span className="font-semibold">{hit.text}</span>
        <span className="ml-2 text-muted-foreground text-xs">
          {strings.kinds[hit.kind as SimileKind]} ·{" "}
          {strings.confidence[hit.confidence]}
        </span>
      </p>
      {hit.vehicle ? (
        <p className="text-muted-foreground text-xs">
          {t(strings.compared, { vehicle: hit.vehicle })}
        </p>
      ) : null}
      <p className="text-muted-foreground text-sm italic">{context}</p>
      {hit.note ? <p className={HINT}>{strings.notes[hit.note]}</p> : null}
    </li>
  );
}

export default function SimileFinderWidget() {
  const strings = useToolUi(SIMILE_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [showPossible, setShowPossible] = useState(true);
  const normalized = text.replace(RE_NEWLINES, "\n");
  const hasText = normalized.trim().length > 0;

  const result = useMemo(
    () => (hasText ? findSimiles(normalized) : null),
    [hasText, normalized]
  );
  const shown = (result?.hits ?? []).filter(
    (hit) => showPossible || hit.confidence === "likely"
  );
  const likely =
    result?.hits.filter((hit) => hit.confidence === "likely").length ?? 0;
  const possible = (result?.hits.length ?? 0) - likely;

  const matches: TextMatch[] = shown.map((hit) => ({
    start: hit.start,
    end: hit.end,
    category: hit.confidence,
    title: strings.confidence[hit.confidence],
  }));
  const samples = [
    [strings.sampleBurns, strings.sampleBurnsText],
    [strings.sampleWordsworth, strings.sampleWordsworthText],
    [strings.sampleTennyson, strings.sampleTennysonText],
    [strings.sampleTricky, strings.sampleTrickyText],
  ] as const;
  const listText = shown.map((hit) => hit.text).join("\n");

  return (
    <>
      <TextAreaField
        id={`${ID}-text`}
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={7}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.samplesLabel}</span>
        {samples.map(([label, body]) => (
          <button
            className={BUTTON_SECONDARY}
            key={label}
            onClick={() => setText(body)}
            type="button"
          >
            {label}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <Checkbox
        checked={showPossible}
        label={strings.possibleLabel}
        onChange={setShowPossible}
      />

      {result ? (
        <section aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <StatGrid>
            <StatCard emphasis label={strings.statLikely} value={fmt(likely)} />
            <StatCard label={strings.statPossible} value={fmt(possible)} />
            <StatCard
              label={strings.statSetAside}
              value={fmt(result.filtered.length)}
            />
          </StatGrid>
          <HighlightedText
            label={strings.highlightLabel}
            matches={matches}
            text={normalized}
            tones={{ likely: "emerald", possible: "amber" }}
          />
          <p className={HINT}>{strings.legend}</p>
          {shown.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>
                {tPlural(strings.count, locale, shown.length, {
                  count: fmt(shown.length),
                })}
              </h3>
              <ul className="space-y-3">
                {shown.map((hit) => (
                  <HitCard
                    context={sentenceAround(normalized, hit.start, hit.end)}
                    hit={hit}
                    key={`${hit.start}-${hit.end}`}
                    strings={strings}
                  />
                ))}
              </ul>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyList}
                text={listText}
              />
            </div>
          ) : (
            <Notice>{strings.noneFound}</Notice>
          )}
          {result.filtered.length > 0 ? (
            <section className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.setAsideHeading}</h3>
              <p className={HINT}>{strings.setAsideHint}</p>
              <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
                {result.filtered.map((item) => (
                  <li key={`${item.start}-${item.end}`}>
                    <span className="font-medium">{item.text}</span>
                    <span className="text-muted-foreground">
                      {" — "}
                      {strings.reasons[item.reason as FilterReason]}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <p className={HINT}>{strings.metaphorNote}</p>
          <p className={HINT}>{strings.methodNote}</p>
          <p className={HINT}>{strings.sampleCredit}</p>
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
